/** @format */

import dayjs from 'dayjs'

export interface ScheduleSegment {
  shiftIndex: number
  shiftName: string
  start: number
  end: number
  color: string
}

export interface DaySchedule {
  date: string
  dateLabel: string
  dayOfWeek: string
  segments: ScheduleSegment[]
}

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

function parseChangeTime(changeTime: string): { hour: number; minute: number } {
  const [hour = 0, minute = 0] = (changeTime || '00:00').split(':').map(Number)
  return { hour, minute }
}

const PERIOD_DAY_MS = 24 * 60 * 60 * 1000

/** 第一班：开始时间 到 当天换班时间；后续班：换班时间到次日换班时间 */
function getPeriodBounds(
  startTime: string,
  changeTime: string,
  period: 'day' | 'week'
): { firstStart: number; firstEnd: number; periodMs: number } {
  const start = dayjs(startTime)
  const { hour, minute } = parseChangeTime(changeTime)
  const firstStart = start.valueOf()

  const changeMomentSameDay = start
    .startOf('day')
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
  let firstEnd: number

  if (period === 'week') {
    // 按周：每班不超过7天。换班00:00时第一班=7天整；否则第一班=7天内最后换班时刻（03-11 00:00 → 03-17 05:03）
    firstEnd =
      hour === 0 && minute === 0
        ? start.add(7, 'day').valueOf()
        : start.add(6, 'day').hour(hour).minute(minute).second(0).millisecond(0).valueOf()
    return {
      firstStart,
      firstEnd,
      periodMs: 7 * PERIOD_DAY_MS
    }
  }

  // 按天：第一班 = 开始时间 到 当天换班时间（仅换班时间晚于开始时间）
  // 若开始时间与当天换班时间相同，则第一班延续到次日换班时间，避免首班时长为 0。
  if (changeMomentSameDay.isAfter(start)) {
    firstEnd = changeMomentSameDay.valueOf()
  } else {
    firstEnd = start.add(1, 'day').startOf('day').hour(hour).minute(minute).valueOf()
  }

  return {
    firstStart,
    firstEnd,
    periodMs: PERIOD_DAY_MS
  }
}

function getShiftIndexAt(
  timestamp: number,
  firstStart: number,
  firstEnd: number,
  periodMs: number,
  shiftCount: number
): number {
  if (shiftCount === 0) return 0
  if (timestamp < firstStart) return 0
  if (timestamp < firstEnd) return 0 // 第一班
  const elapsed = timestamp - firstEnd
  const periodIndex = 1 + Math.floor(elapsed / periodMs)
  return periodIndex % shiftCount
}

/** 判断某天是否与排班有重叠（需展示） */
function isDayInScheduleRange(
  dayStart: number,
  dayEnd: number,
  startTime: string,
  endTime: string
): boolean {
  const start = dayjs(startTime).valueOf()
  if (dayEnd < start) return false // 整天在开始时间之前
  if (!endTime || !endTime.trim()) return true
  const end = dayjs(endTime).valueOf() // 使用精确结束时间，不超出
  // endTime 视为排他（exclusive）：例如 2026-04-02 00:00 代表到这刻为止，
  // 不应再展示 04-02 这一天的轮转。
  return dayStart < end
}

/** 生成值班轮转预览数据 */
export function generateDutySchedule(
  dutyShift: { ContactName: string; ContactID: number }[][],
  period: 'day' | 'week',
  changeTime: string,
  startTime: string,
  endTime: string,
  previewStart: number,
  previewEnd: number,
  colorList: string[]
): DaySchedule[] {
  const result: DaySchedule[] = []
  const shiftCount = dutyShift?.length || 0

  if (!startTime || shiftCount === 0) {
    return result
  }

  const { firstStart, firstEnd, periodMs } = getPeriodBounds(startTime, changeTime, period)
  const { hour, minute } = parseChangeTime(changeTime)
  const scheduleStart = dayjs(startTime).valueOf()
  const scheduleEnd = endTime?.trim() ? dayjs(endTime).valueOf() : null // 使用精确结束时间，排班不超出

  const getShiftName = (index: number) =>
    dutyShift[index]?.map(c => c.ContactName).join(', ') || '-'
  const getShiftColor = (index: number) => colorList[index % colorList.length] || '#999'

  let current = dayjs(previewStart).startOf('day')
  const end = dayjs(previewEnd).endOf('day')

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dateStr = current.format('YYYY-MM-DD')
    const dateLabel = current.format('MM-DD')
    const dayOfWeek = WEEKDAYS[current.day()]
    const segments: ScheduleSegment[] = []

    const dayStart = current.startOf('day').valueOf()
    const changeMoment = current.hour(hour).minute(minute).second(0).millisecond(0)
    const dayEnd = current.endOf('day').valueOf()

    if (!isDayInScheduleRange(dayStart, dayEnd, startTime, endTime)) {
      result.push({ date: dateStr, dateLabel, dayOfWeek, segments: [] })
      current = current.add(1, 'day')
      continue
    }

    const effectiveStart = Math.max(dayStart, scheduleStart)
    const effectiveEnd = scheduleEnd ? Math.min(dayEnd, scheduleEnd) : dayEnd

    if (period === 'day') {
      if (hour === 0 && minute === 0) {
        const shiftIndex = getShiftIndexAt(
          effectiveStart,
          firstStart,
          firstEnd,
          periodMs,
          shiftCount
        )
        if (effectiveEnd > effectiveStart) {
          segments.push({
            shiftIndex,
            shiftName: getShiftName(shiftIndex),
            start: effectiveStart,
            end: effectiveEnd,
            color: getShiftColor(shiftIndex)
          })
        }
      } else {
        const changeTs = changeMoment.valueOf()
        // 按天且非 00:00 换班：以当天为基准最多拆成两段
        // 左段：00:00-换班时间；右段：换班时间-24:00
        // 再与排班有效区间取交集，保证每天最多左右两个班次。
        const windows: Array<[number, number]> = [
          [dayStart, changeTs],
          [changeTs, dayEnd]
        ]

        windows.forEach(([winStart, winEnd]) => {
          if (winEnd <= winStart) return
          const segStart = Math.max(winStart, effectiveStart)
          const segEnd = Math.min(winEnd, effectiveEnd)
          if (segEnd <= segStart) return

          const shiftIndex = getShiftIndexAt(segStart, firstStart, firstEnd, periodMs, shiftCount)
          segments.push({
            shiftIndex,
            shiftName: getShiftName(shiftIndex),
            start: segStart,
            end: segEnd,
            color: getShiftColor(shiftIndex)
          })
        })
      }
    } else {
      // 按周：仅在真实周换班边界拆分，而不是每天按换班时间拆分。
      // 这样首日不会出现不必要的“左右两段”。
      const boundaries = [effectiveStart]

      // 首次换班边界 firstEnd 若落在当天有效区间内，则作为拆分点。
      if (firstEnd > effectiveStart && firstEnd < effectiveEnd) {
        boundaries.push(firstEnd)
      } else if (effectiveEnd > firstEnd) {
        // 后续换班边界：firstEnd + n * 7天
        const n = Math.max(0, Math.ceil((effectiveStart - firstEnd) / periodMs))
        const nextBoundary = firstEnd + n * periodMs
        if (nextBoundary > effectiveStart && nextBoundary < effectiveEnd) {
          boundaries.push(nextBoundary)
        }
      }

      boundaries.push(effectiveEnd)

      for (let i = 0; i < boundaries.length - 1; i++) {
        const segStart = boundaries[i]
        const segEnd = boundaries[i + 1]
        if (segEnd <= segStart) continue
        const shiftIndex = getShiftIndexAt(segStart, firstStart, firstEnd, periodMs, shiftCount)
        segments.push({
          shiftIndex,
          shiftName: getShiftName(shiftIndex),
          start: segStart,
          end: segEnd,
          color: getShiftColor(shiftIndex)
        })
      }
    }

    result.push({ date: dateStr, dateLabel, dayOfWeek, segments })
    current = current.add(1, 'day')
  }

  return result
}

export interface MergedSegment {
  shiftIndex: number
  shiftName: string
  start: number
  end: number
  color: string
}

/** 合并连续相同班次的排班段，返回跨天的连续块 */
export function mergeConsecutiveSegments(
  scheduleData: DaySchedule[],
  previewStart: number,
  previewEnd: number
): MergedSegment[] {
  const all: ScheduleSegment[] = []
  for (const day of scheduleData) {
    for (const seg of day.segments || []) {
      all.push(seg)
    }
  }
  all.sort((a, b) => a.start - b.start)

  const merged: MergedSegment[] = []
  const ADJACENT_MS = 1000
  for (const seg of all) {
    const last = merged[merged.length - 1]
    const isAdjacent =
      last && last.shiftIndex === seg.shiftIndex && seg.start - last.end <= ADJACENT_MS
    if (isAdjacent) {
      last.end = seg.end
    } else {
      merged.push({ ...seg })
    }
  }

  const result: MergedSegment[] = []
  const firstSeg = merged[0]
  if (firstSeg && firstSeg.start > previewStart) {
    result.push({
      shiftIndex: -1,
      shiftName: '',
      start: previewStart,
      end: Math.min(firstSeg.start, previewEnd),
      color: 'transparent'
    })
  }
  for (const m of merged) {
    const segStart = Math.max(m.start, previewStart)
    const segEnd = Math.min(m.end, previewEnd)
    if (segStart < segEnd) {
      result.push({ ...m, start: segStart, end: segEnd })
    }
  }
  return result
}
